import type { AxiosInstance } from 'axios';
import type { Announcement, User } from '@/types';
import baseAxios from '@/helpers/forms/axios';

export interface PerformanceIndicator {
    id: number
    monthly_recurring_revenue: number
    yearly_recurring_revenue: number
    daily_volume: number
    new_users: number
    total_users: number
    total_subscribed_users: number
    created_at: string
}

/**
 * `GET /spark/kiosk/performance-indicators` returns an envelope, not a bare
 * list — `indicators` is the time series the charts plot, while `last_month`
 * and `last_year` are single snapshots the summary tiles compare against.
 */
export interface PerformanceIndicatorFeed {
    indicators: PerformanceIndicator[]
    last_month: PerformanceIndicator | null
    last_year: PerformanceIndicator | null
}

export interface RevenueTotals {
    yearlyRecurringRevenue: number
    monthlyRecurringRevenue: number
    totalVolume: number
}

export interface PlanSubscribers {
    name: string
    interval: string
    count: number
    trialing: number
}

export interface KioskUserProfile {
    user: User
    invoices: { id: string, date: string, total: string }[]
    tokens: { id: string, name: string }[]
}

interface KioskApi {
    searchUsers: (query: string) => Promise<User[]>
    fetchUserProfile: (userId: number) => Promise<KioskUserProfile>
    fetchAnnouncements: () => Promise<Announcement[]>
    createAnnouncement: (payload: { body: string, action_text: string | null, action_url: string | null }) => Promise<void>
    updateAnnouncement: (announcementId: string, payload: { body: string, action_text: string | null, action_url: string | null }) => Promise<void>
    deleteAnnouncement: (announcementId: string) => Promise<void>
    fetchPerformanceIndicators: () => Promise<PerformanceIndicatorFeed>
    fetchRevenueTotals: () => Promise<RevenueTotals>
    fetchPlanSubscribers: () => Promise<PlanSubscribers[]>
    fetchTrialingCount: () => Promise<number>
    applyDiscount: (userId: number, payload: Record<string, unknown>) => Promise<void>
    impersonateUrl: (userId: number) => string
    resetLabelCountUrl: (userId: number, type: string) => string
}

export default (axios: AxiosInstance = baseAxios): KioskApi => ({
    searchUsers: async (query: string): Promise<User[]> => {
        const response = await axios.post<User[]>('/spark/kiosk/users/search', { query });
        return response.data;
    },
    fetchUserProfile: async (userId: number): Promise<KioskUserProfile> => {
        const response = await axios.get<KioskUserProfile>(`/spark/kiosk/users/${userId}/profile`);
        return response.data;
    },
    fetchAnnouncements: async (): Promise<Announcement[]> => {
        const response = await axios.get<Announcement[]>('/spark/kiosk/announcements');
        return response.data;
    },
    createAnnouncement: async (payload: { body: string, action_text: string | null, action_url: string | null }): Promise<void> => {
        await axios.post('/spark/kiosk/announcements', payload);
    },
    updateAnnouncement: async (announcementId: string, payload: { body: string, action_text: string | null, action_url: string | null }): Promise<void> => {
        await axios.put(`/spark/kiosk/announcements/${announcementId}`, payload);
    },
    deleteAnnouncement: async (announcementId: string): Promise<void> => {
        await axios.delete(`/spark/kiosk/announcements/${announcementId}`);
    },
    fetchPerformanceIndicators: async (): Promise<PerformanceIndicatorFeed> => {
        const response = await axios.get<PerformanceIndicatorFeed>('/spark/kiosk/performance-indicators');
        return response.data;
    },
    fetchRevenueTotals: async (): Promise<RevenueTotals> => {
        const response = await axios.get<RevenueTotals>('/spark/kiosk/performance-indicators/revenue');
        return response.data;
    },
    fetchPlanSubscribers: async (): Promise<PlanSubscribers[]> => {
        const response = await axios.get<PlanSubscribers[]>('/spark/kiosk/performance-indicators/plans');
        return response.data;
    },
    fetchTrialingCount: async (): Promise<number> => {
        const response = await axios.get<number>('/spark/kiosk/performance-indicators/trialing');
        return response.data;
    },
    applyDiscount: async (userId: number, payload: Record<string, unknown>): Promise<void> => {
        await axios.post(`/spark/kiosk/users/discount/${userId}`, payload);
    },
    impersonateUrl: (userId: number): string => `/spark/kiosk/users/impersonate/${userId}`,
    resetLabelCountUrl: (userId: number, type: string): string => `/spark/kiosk/users/${userId}/reset/${type}`,
});
